/**
 * Global duplicate-toast guard.
 *
 * The axios response interceptor (src/axios.tsx) toasts the server's `EM` for
 * every failed HTTP response, and many components ALSO toast that same `EM`
 * from their catch block — so one user action stacks two identical toasts.
 * An audit found 44 such call sites; fixing them one by one is endless and
 * regresses the moment someone adds a new catch block.
 *
 * react-toastify already dedupes by `toastId`: while a toast with a given id
 * is on screen, a second call with the same id is ignored. This installs a
 * thin wrapper that derives an id from the message text when the caller did
 * not supply one, so identical messages collapse into a single toast no
 * matter who fired them. Callers that pass their own `toastId` keep it, and
 * non-string content (JSX) is left untouched.
 *
 * Call once at app startup, before anything renders.
 */
import { toast, ToastContent, ToastOptions, Id } from 'react-toastify';

type ToastFn = (content: ToastContent, options?: ToastOptions) => Id;

/** Variants that share the same signature and are worth deduping. */
const VARIANTS = ['error', 'warn', 'warning', 'success', 'info'] as const;

/** djb2 — short, stable, collision-safe enough for toast text. */
const idFromContent = (variant: string, content: ToastContent): string | null => {
  if (typeof content !== 'string' && typeof content !== 'number') return null;
  const text = String(content);
  if (!text) return null;
  let hash = 5381;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  }
  return `dd-${variant}-${hash >>> 0}`;
};

let installed = false;

export const installToastDedupe = (): void => {
  if (installed) return;
  installed = true;

  VARIANTS.forEach((variant) => {
    const target = toast as unknown as Record<string, ToastFn>;
    const original = target[variant];
    if (typeof original !== 'function') return;

    target[variant] = (content: ToastContent, options?: ToastOptions): Id => {
      if (options?.toastId !== undefined) return original(content, options);
      const toastId = idFromContent(variant, content);
      if (!toastId) return original(content, options);
      return original(content, { ...options, toastId });
    };
  });
};
